/**
 * Financial actuals — SAP backend implementation.
 *
 * Part of the isolated `sap-backend/` side project (see ../README.md). Backs the
 * dataService `getFinancialActuals(projectId)` contract with live ACDOCA postings
 * from ZC_M12_WBS_ACTUALS (the same universal-journal view periodic actuals use,
 * read here at its raw WBS × period × cost-element grain).
 *
 * This entity deliberately uses the generic odataClient directly (rather than
 * delegating to wbsActualsService) so the reusable client path is exercised and
 * future entities have a worked example to copy.
 */

import { S4_PROXY_BASE } from '../config'
import { odataGetCollection, odataLiteral } from '../odataClient'

/** ZC_M12_WBS_ACTUALS exposed via ZUI_M12_WBS_ACTUALS_O4. The runtime path uses
 *  `srvd` even though SAP publishes `srvd_a2x` (see currencyRatesService.ts). */
const WBS_ACTUALS_PATH = `${S4_PROXY_BASE}/zui_m12_wbs_actuals_o4/srvd/sap/z_m12_wbs_actuals_sd/0001/WbsActuals`

/** Raw row shape as ZC_M12_WBS_ACTUALS serializes it. NUMC fields come back
 *  without leading zeros (e.g. FiscalPeriod "1".."12"). */
interface RawWbsActualRow {
  CompanyCode: string
  ProjectID: string
  WbsElement: string
  FiscalYear: string
  FiscalPeriod: string
  CostElement: string
  CostElementCategory: string
  ActualCost: number
  ActualQuantity: number
  Currency: string
  Unit: string
}

/** Financial-actuals row in the app's camelCase convention. One row per
 *  WBS × period × cost element — the raw posting grain from ACDOCA. */
export interface SapFinancialActualRow {
  projectId: string
  companyCode: string
  wbsId: string
  period: string // 'YYYY-MM'
  costElement: string
  costElementCategory: string
  amount: number
  currency: string
  quantity: number
  unit: string
}

function recentFiscalYears(): string[] {
  const now = new Date().getFullYear()
  return [now, now - 1, now - 2].map(String)
}

/**
 * Read posted financial actuals for a project from ACDOCA. Without a projectId
 * the universal journal cannot be cheaply scoped, so we return [] and warn —
 * callers pass a project.
 */
export async function getFinancialActuals(projectId?: string): Promise<SapFinancialActualRow[]> {
  if (!projectId) {
    console.warn('[sap-backend] getFinancialActuals requires a projectId in sap mode; returning []')
    return []
  }

  const years = recentFiscalYears()
  const perYear = await Promise.all(
    years.map(async (fy) => {
      const filter = `ProjectID eq '${odataLiteral(projectId)}' and FiscalYear eq '${odataLiteral(fy)}'`
      const url = `${WBS_ACTUALS_PATH}?$filter=${encodeURIComponent(filter)}&$top=5000`
      try {
        return await odataGetCollection<RawWbsActualRow>(url)
      } catch (err) {
        console.warn(`[sap-backend] financial actuals read for ${projectId} FY${fy} failed:`, err)
        return []
      }
    }),
  )

  return perYear.flat().map((r) => {
    const p = parseInt(r.FiscalPeriod, 10)
    const period = `${r.FiscalYear}-${String(Number.isFinite(p) ? p : 0).padStart(2, '0')}`
    return {
      projectId: r.ProjectID || projectId,
      companyCode: r.CompanyCode,
      wbsId: r.WbsElement,
      period,
      costElement: r.CostElement,
      costElementCategory: r.CostElementCategory,
      amount: r.ActualCost ?? 0,
      currency: r.Currency,
      quantity: r.ActualQuantity ?? 0,
      unit: r.Unit,
    }
  })
}
